/**
 * lazyRetry — React.lazy with stale-chunk auto-recovery.
 *
 * Route-level code splitting means chunk filenames rotate every deploy. A
 * session opened BEFORE a deploy holds the old index.html, so navigating to a
 * not-yet-visited route 404s its chunk → React.lazy rejects → white screen
 * (user report 2026-06-11: "went from the report to the brief and received an
 * error and the page was white"). One automatic reload fetches the fresh
 * index.html and resolves it; a sessionStorage latch prevents a reload loop
 * when the failure is something other than a stale deploy.
 */

import { lazy } from 'react';

const LATCH = 'avium-chunk-reloaded';

/** Call once after the app mounts successfully — re-arms the latch. */
export function clearChunkReloadLatch(): void {
  sessionStorage.removeItem(LATCH);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRetry<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (sessionStorage.getItem(LATCH) !== '1') {
        sessionStorage.setItem(LATCH, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {}); // halt — the reload takes over
      }
      throw err; // second failure in a row = a real error; let the boundary show it
    }),
  );
}
